import { useEffect } from "react";

// Filing a field into a folder is a native HTML5 drag, and native drag does NOT scroll the page for
// you — on a long field list the target folder is often off-screen, leaving the user holding a drag
// they cannot complete.

export const EDGE_ZONE_PX = 72;
export const MAX_SPEED_PX = 18;

// How far to scroll this frame, given the pointer's viewport Y and the scroll container's edges.
// Negative scrolls up, positive down, 0 means the pointer is in the neutral middle band.
// Exported for testing — the geometry is the part worth pinning; the rAF plumbing is not.
export function autoScrollDelta(
  clientY: number,
  top: number,
  bottom: number,
  edge: number = EDGE_ZONE_PX,
  maxSpeed: number = MAX_SPEED_PX,
): number {
  const height = bottom - top;
  if (height <= 0) return 0;
  // On a short container a full-size zone at each end would overlap in the middle, so every position
  // would scroll and the list could never sit still. Cap each zone at a third of the height.
  const zone = Math.min(edge, Math.floor(height / 3));
  if (zone <= 0) return 0;
  const clamp01 = (n: number) => Math.min(1, Math.max(0, n));
  // Past the edge entirely (pointer dragged above/below the container) clamps to full speed rather than
  // running away — depth would otherwise exceed 1.
  if (clientY < top + zone)
    return -Math.ceil(clamp01((top + zone - clientY) / zone) * maxSpeed);
  if (clientY > bottom - zone)
    return Math.ceil(clamp01((clientY - (bottom - zone)) / zone) * maxSpeed);
  return 0;
}

// Nearest ancestor that actually scrolls vertically. `overflow-x-auto` wrappers (the table has one) report
// `overflowY: auto` in some engines, so the scrollHeight check is what keeps us off them.
export function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node) {
    const oy = getComputedStyle(node).overflowY;
    if (
      (oy === "auto" || oy === "scroll" || oy === "overlay") &&
      node.scrollHeight > node.clientHeight
    )
      return node;
    node = node.parentElement;
  }
  return null;
}

export function useDragAutoScroll(
  active: boolean,
  ref: { current: HTMLElement | null },
): void {
  useEffect(() => {
    if (!active) return;
    const container =
      findScrollParent(ref.current) ??
      (document.scrollingElement as HTMLElement | null);
    if (!container) return;

    let raf = 0;
    let clientY: number | null = null;

    // `dragover` is the only pointer signal a native drag emits (dragstart/dragend carry no useful
    // position, and mousemove does not fire mid-drag). Capture-phase + passive: we only ever READ the
    // position. Never preventDefault here — that is what marks an element a valid drop target, and doing
    // it document-wide would make the whole page droppable.
    const onDragOver = (e: DragEvent) => {
      clientY = e.clientY;
    };
    const stop = () => {
      clientY = null;
    };

    const tick = () => {
      if (clientY !== null) {
        const r = container.getBoundingClientRect();
        const d = autoScrollDelta(clientY, r.top, r.bottom);
        if (d !== 0) container.scrollTop += d;
      }
      raf = requestAnimationFrame(tick);
    };

    document.addEventListener("dragover", onDragOver, {
      capture: true,
      passive: true,
    });
    // A drop outside any target fires `dragend` only, so both are needed to reliably stop scrolling.
    document.addEventListener("dragend", stop, true);
    document.addEventListener("drop", stop, true);
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("dragover", onDragOver, true);
      document.removeEventListener("dragend", stop, true);
      document.removeEventListener("drop", stop, true);
    };
  }, [active, ref]);
}
